import React from 'react'
import { cn } from '@/lib/utils'

interface SpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg'
  className?: string
}

const sizeClasses = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-10 h-10',
}

export function Spinner({ size = 'md', className }: SpinnerProps) {
  return (
    <svg
      className={cn('animate-spin text-white/60', sizeClasses[size], className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  )
}

interface SkeletonProps {
  className?: string
  variant?: 'text' | 'card' | 'avatar' | 'button' | 'table-row'
  lines?: number
}

export function Skeleton({ className, variant = 'text', lines = 1 }: SkeletonProps) {
  if (variant === 'card') {
    return (
      <div className={cn('ds-tile p-4 space-y-3', className)}>
        <div className="h-4 shimmer rounded w-2/3" />
        <div className="h-3 shimmer rounded w-full" />
        <div className="h-3 shimmer rounded w-4/5" />
      </div>
    )
  }

  if (variant === 'avatar') {
    return (
      <div className={cn('w-10 h-10 rounded-full shimmer', className)} />
    )
  }

  if (variant === 'button') {
    return <div className={cn('h-9 w-24 shimmer rounded-lg', className)} />
  }

  if (variant === 'table-row') {
    return (
      <div className={cn('flex gap-4 py-3 px-4 hairline-b', className)}>
        <div className="h-4 shimmer rounded w-1/4" />
        <div className="h-4 shimmer rounded w-1/4" />
        <div className="h-4 shimmer rounded w-1/6" />
        <div className="h-4 shimmer rounded w-1/6" />
        <div className="h-4 shimmer rounded w-1/8" />
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={`skeleton-${i}`} className={cn('h-4 shimmer rounded', i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full')} />
      ))}
    </div>
  )
}
